import {getConsentManagementConfig} from './consentManagement';
import {extendPriceFloorConfig} from './priceFloors';
import {applyPlaceholders} from '../../utils/placeholder';
import * as storage from '../../utils/storage';
import {getDMOI, getRandomizedDMOI} from "../../services/userService";
import {extendDSAModuleConfig} from "./dsaModule";
import {getPageTargeting} from '../../services/targeting';

export function getPlaceholders(): Record<string, string> {
	return {
		'PWID': storage.getFromCookie('authId'),
		'domain': window.location.hostname,
		'pageUrl': `${window.location.origin}${window.location.pathname}`,
		'userId': getDMOI(),
		'randomizedUserId': getRandomizedDMOI()
	};
}

export function getModuleConfig(config: PrebidConfig) {
	const prebidModuleConfig: AnyObject = Object.assign(
		JSON.parse(JSON.stringify(config.additionalConfig ?? {})),
		getConsentManagementConfig(config),
	);

	extendPriceFloorConfig(prebidModuleConfig);
	extendDSAModuleConfig(prebidModuleConfig);

	return applyPlaceholders(prebidModuleConfig, getPlaceholders());
}

type BidderConfig = {
	bidders: Array<string>
	config: {
		ortb2?: {
			site?: {
				ext?: {
					data?: AnyObject
				}
			}
		}
	}
};

export function getBidderModuleConfigs(config: PrebidConfig) {
	const additionalBidderConfigs = JSON.parse(JSON.stringify(config.additionalBidderConfigs ?? [])),
		coveredBidders: Set<string> = new Set(additionalBidderConfigs.flatMap((bc: BidderConfig) => bc.bidders)),
		bidders = config.bidders ?? {},
		placeholders = getPlaceholders(),
		pageTargeting = getPageTargeting(),
		bidderConfigs = additionalBidderConfigs.flatMap((bidderConfig: BidderConfig) =>
			applyPlaceholders(enrichBidderConfigs(bidderConfig, bidders, pageTargeting), placeholders)),
		targetingOnlyBidderConfigs =
			applyPlaceholders(enrichTargetingOnlyBidderConfigs(bidders, coveredBidders, pageTargeting), placeholders);

	return [...bidderConfigs, ...targetingOnlyBidderConfigs];
}

function enrichBidderConfigs(bidderConfig: BidderConfig, bidders: PrebidConfig["bidders"], pageTargeting: Targeting) {
	const targetingData = getBidderTargeting(bidderConfig.bidders, bidders, pageTargeting),
		bidderConfigsWithoutTargeting = getBidderConfigsWithoutTargeting(bidderConfig, targetingData),
		bidderConfigsWithTargeting = getBidderConfigsWithTargeting(bidderConfig, targetingData);

	return [...bidderConfigsWithoutTargeting, ...bidderConfigsWithTargeting];
}

function getBidderConfigsWithoutTargeting(bidderConfig: BidderConfig, targetingData: Record<string, {
	[p: string]: TargetValue | TargetValue[]
}>): BidderConfig[] {
	const bidders = bidderConfig.bidders.filter(bidder => !targetingData[bidder]);

	if (!bidders.length) {
		return [];
	}

	return [{
		bidders: bidders,
		config: bidderConfig.config
	}];
}

function getBidderConfigsWithTargeting(bidderConfig: BidderConfig, targetingData: Record<string, {
	[p: string]: TargetValue | TargetValue[]
}>): BidderConfig[] {
	return bidderConfig.bidders
		.filter(b => targetingData[b])
		.map((bidder) => {
			const enrichedConfig = enrichBidderConfig(targetingData[bidder], bidderConfig.config);

			return {
				bidders: [bidder],
				config: enrichedConfig
			};
		});
}

function enrichTargetingOnlyBidderConfigs(bidders: PrebidConfig["bidders"], coveredBidders: Set<string>, pageTargeting: Targeting): Array<BidderConfig> {
	return Object.keys(bidders)
		.filter((bidderName) => !coveredBidders.has(bidderName))
		.flatMap((bidderName) => {
			const targetingData = getBidderTargeting([bidderName], bidders, pageTargeting);

			if (!Object.keys(targetingData[bidderName] ?? {}).length) {
				return [];
			}

			return [{
				bidders: [bidderName],
				config: enrichBidderConfig(targetingData[bidderName], {})
			}];
		});
}

function getBidderTargeting(bidderNames: Array<string>, bidders: PrebidConfig["bidders"], pageTargeting: Targeting) {
	const targetingFields: Record<string, { [x: string]: TargetValue | TargetValue[]; }> = {};

	bidderNames.forEach((bidderName) => {
		if (!bidders[bidderName]?.targetingFields) {
			return;
		}

		const currentTargetingFields = bidders[bidderName].targetingFields;

		targetingFields[bidderName] = currentTargetingFields.reduce((agg, field) => {
			if (pageTargeting?.[field]) {
				agg[field] = pageTargeting[field];
			}

			return agg;
		}, {} as { [x: string]: TargetValue | TargetValue[]; });
	});

	return targetingFields;
}

function enrichBidderConfig(targetingData: AnyObject, existingConfig?: BidderConfig["config"]) {
	const existingData = existingConfig?.ortb2?.site?.ext?.data ?? {};

	return {
		...existingConfig,
		ortb2: {
			...existingConfig?.ortb2,
			site: {
				...existingConfig?.ortb2?.site,
				ext: {
					...existingConfig?.ortb2?.site?.ext,
					data: {...targetingData, ...existingData}
				}
			}
		}
	};
}

export function getBidderSettingsConfigs(config: PrebidConfig) {
	const bidderSettings = JSON.parse(JSON.stringify(config.bidderSettings ?? {})),
		placeholders = getPlaceholders();

	return applyPlaceholders(bidderSettings, placeholders);
}


export default {
	getPlaceholders,
	getModuleConfig,
	getBidderModuleConfigs,
	getBidderSettingsConfigs
};
